import React, { useState, useEffect } from 'react';
import { usersApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Users, Shield, User, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function UsersPage() {
  const { canDelete } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(id);
      setUsers(users.filter(u => u.id !== id));
      toast.success('User deleted');
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 skeleton rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Create and manage team members</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingUser(null); // Clear editing state when dialog closes
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingUser(null)} data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <UserDialog
            user={editingUser}
            onClose={() => {
              setDialogOpen(false);
              setEditingUser(null); // Clear editing state
            }}
            onSave={() => {
              loadUsers();
              setEditingUser(null); // Clear editing state after save
            }}
          />
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-slate-500 text-sm">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
                <p className="text-slate-500 text-sm">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'founder').length}</p>
                <p className="text-slate-500 text-sm">Founders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'member').length}</p>
                <p className="text-slate-500 text-sm">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-slate-600">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      {user.name}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      user.role === 'admin' ? 'bg-amber-50 text-amber-700 border-amber-200' : 
                      user.role === 'founder' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                      'bg-blue-50 text-blue-700 border-blue-200'
                    }>
                      {user.role === 'admin' ? <Shield className="w-3 h-3 mr-1" /> : 
                       user.role === 'founder' ? <Crown className="w-3 h-3 mr-1" /> :
                       <User className="w-3 h-3 mr-1" />}
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setEditingUser(user); setDialogOpen(true); }}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {canDelete() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

const DEFAULT_USER_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'member',
};

function UserDialog({ user, onClose, onSave }) {
  const [form, setForm] = useState(DEFAULT_USER_FORM);
  const [loading, setLoading] = useState(false);

  // Re-hydrate form state when user changes (edit vs create mode)
  useEffect(() => {
    if (user?.id) {
      // Edit mode: populate form with user data
      setForm({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'member',
      });
    } else {
      // Create mode: reset to default
      setForm(DEFAULT_USER_FORM);
    }
  }, [user?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Decide create vs update based ONLY on user?.id
      if (user?.id) {
        // UPDATE existing user
        const updateData = { name: form.name, role: form.role };
        if (form.password) updateData.password = form.password;
        await usersApi.update(user.id, updateData);
        toast.success('User updated');
      } else {
        // CREATE new user
        await usersApi.create(form);
        toast.success('User created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{user?.id ? 'Edit User' : 'Create User'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            placeholder="Full name"
            required
            data-testid="user-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({...form, email: e.target.value})}
            placeholder="email@example.com"
            required
            disabled={!!user?.id}
            data-testid="user-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{user?.id ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({...form, password: e.target.value})}
            placeholder="••••••••"
            required={!user?.id}
            data-testid="user-password-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Role</Label>
          <Select value={form.role || 'member'} onValueChange={(v) => setForm({...form, role: v})}>
            <SelectTrigger data-testid="user-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin (Full Access)</SelectItem>
              <SelectItem value="founder">Founder (View All, No Delete)</SelectItem>
              <SelectItem value="member">Team Member (Own Data Only)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-user-btn">
            {loading ? 'Saving...' : 'Save User'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
